import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { supabase } from '../../services/supabase'
import { SUPABASE_ANON_KEY, SUPABASE_URL } from '../../utils/constants'
import { currentLeaveYear } from './constants'
import { useSimpleHqAccess } from './useSimpleHqAccess'

type RosterRow = { user_id: string; email: string; role: string }
type LeaveType = { id: string; name: string; default_days: number }

async function callHqPeople(pathQuery: string, init: RequestInit = {}) {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Not signed in')
  const url = `${SUPABASE_URL}/functions/v1/simple-hq-people${pathQuery}`
  const res = await fetch(url, {
    ...init,
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      apikey: SUPABASE_ANON_KEY,
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error(json?.error || json?.message || `HTTP ${res.status}`)
  }
  return json
}

export function LeavePeopleView() {
  const { user } = useAuth()
  const { companyId } = useSimpleHqAccess()
  const [roster, setRoster] = useState<RosterRow[]>([])
  const [types, setTypes] = useState<LeaveType[]>([])
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<string | null>(null)
  const [year, setYear] = useState(currentLeaveYear())
  const [entitlements, setEntitlements] = useState<Record<string, number>>({})
  const [remaining, setRemaining] = useState<Record<string, number>>({})
  const [bulkIds, setBulkIds] = useState<Set<string>>(new Set())
  const [bulkType, setBulkType] = useState('')
  const [bulkDays, setBulkDays] = useState('15')
  const [adjType, setAdjType] = useState('')
  const [adjDelta, setAdjDelta] = useState('1')
  const [adjReason, setAdjReason] = useState('')
  const [csvText, setCsvText] = useState('')
  const [msg, setMsg] = useState<string | null>(null)
  const [addEmail, setAddEmail] = useState('')
  const [addName, setAddName] = useState('')
  const [adding, setAdding] = useState(false)
  const [removingId, setRemovingId] = useState<string | null>(null)

  const loadRoster = async () => {
    const [{ data: r }, { data: t }] = await Promise.all([
      supabase.rpc('simple_hq_roster'),
      supabase
        .from('leave_types')
        .select('id, name, default_days')
        .eq('company_id', companyId)
        .eq('active', true)
        .order('sort_order'),
    ])
    setRoster((r as RosterRow[]) || [])
    setTypes((t as LeaveType[]) || [])
    if (t?.[0]) {
      setBulkType(t[0].id)
      setAdjType(t[0].id)
    }
  }

  useEffect(() => {
    void loadRoster()
  }, [companyId])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return roster
    return roster.filter((r) => r.email.toLowerCase().includes(q) || r.role.includes(q))
  }, [roster, search])

  const loadPerson = async (userId: string) => {
    setSelected(userId)
    setMsg(null)
    const ent: Record<string, number> = {}
    const rem: Record<string, number> = {}
    const { data: ents } = await supabase
      .from('leave_entitlements')
      .select('leave_type_id, entitlement_days')
      .eq('company_id', companyId)
      .eq('user_id', userId)
      .eq('leave_year', year)
    for (const e of ents || []) ent[e.leave_type_id] = Number(e.entitlement_days)
    for (const t of types) {
      if (ent[t.id] == null) ent[t.id] = Number(t.default_days)
      const { data } = await supabase.rpc('leave_remaining_days', {
        p_user_id: userId,
        p_leave_type_id: t.id,
        p_leave_year: year,
      })
      rem[t.id] = typeof data === 'number' ? data : Number(data)
    }
    setEntitlements(ent)
    setRemaining(rem)
  }

  useEffect(() => {
    if (selected) void loadPerson(selected)
  }, [year, types.length])

  const saveEntitlement = async (leaveTypeId: string, days: number) => {
    if (!selected) return
    const { error } = await supabase.from('leave_entitlements').upsert(
      {
        company_id: companyId,
        user_id: selected,
        leave_type_id: leaveTypeId,
        leave_year: year,
        entitlement_days: days,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'company_id,user_id,leave_type_id,leave_year' },
    )
    if (error) {
      alert(error.message)
      return
    }
    setMsg('Entitlement saved')
    await loadPerson(selected)
  }

  const applyBulk = async () => {
    if (!bulkType || bulkIds.size === 0) return
    const days = Number(bulkDays)
    const rows = [...bulkIds].map((uid) => ({
      company_id: companyId,
      user_id: uid,
      leave_type_id: bulkType,
      leave_year: year,
      entitlement_days: days,
      updated_at: new Date().toISOString(),
    }))
    const { error } = await supabase
      .from('leave_entitlements')
      .upsert(rows, { onConflict: 'company_id,user_id,leave_type_id,leave_year' })
    if (error) alert(error.message)
    else {
      setMsg(`Applied ${days} days to ${rows.length} people`)
      if (selected) await loadPerson(selected)
    }
  }

  const adjust = async () => {
    if (!selected || !adjType || !adjReason.trim()) {
      alert('Select person, type, and reason')
      return
    }
    const { error } = await supabase.from('leave_balance_adjustments').insert({
      company_id: companyId,
      user_id: selected,
      leave_type_id: adjType,
      leave_year: year,
      delta_days: Number(adjDelta),
      reason: adjReason.trim(),
      created_by: user?.id,
    })
    if (error) alert(error.message)
    else {
      setAdjReason('')
      setMsg('Adjustment recorded')
      await loadPerson(selected)
    }
  }

  const importCsv = async () => {
    const lines = csvText
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean)
    const byEmail = new Map(roster.map((r) => [r.email.toLowerCase(), r.user_id]))
    const byTypeName = new Map(types.map((t) => [t.name.toLowerCase(), t.id]))
    const rows: {
      company_id: string
      user_id: string
      leave_type_id: string
      leave_year: number
      entitlement_days: number
    }[] = []
    for (const line of lines) {
      if (line.toLowerCase().startsWith('email')) continue
      const [email, typeKey, daysStr] = line.split(',').map((x) => x.trim())
      const uid = byEmail.get(email.toLowerCase())
      const tid = byTypeName.get(typeKey.toLowerCase()) || types.find((t) => t.id === typeKey)?.id
      if (!uid || !tid) continue
      rows.push({
        company_id: companyId,
        user_id: uid,
        leave_type_id: tid,
        leave_year: year,
        entitlement_days: Number(daysStr),
      })
    }
    if (!rows.length) {
      alert('No valid CSV rows (email,leave type name,days)')
      return
    }
    const { error } = await supabase
      .from('leave_entitlements')
      .upsert(rows, { onConflict: 'company_id,user_id,leave_type_id,leave_year' })
    if (error) alert(error.message)
    else setMsg(`Imported ${rows.length} entitlement rows`)
  }

  const exportReport = () => {
    const header = ['email', 'role', ...types.map((t) => `${t.name}_remaining`)]
    const lines = [header.join(',')]
    for (const r of filtered) {
      lines.push([r.email, r.role, ...types.map(() => '')].join(','))
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `simple-hq-leave-roster-${year}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const toggleBulk = (id: string) => {
    setBulkIds((prev) => {
      const n = new Set(prev)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })
  }

  const addPerson = async (e: React.FormEvent) => {
    e.preventDefault()
    const email = addEmail.trim().toLowerCase()
    if (!email) return
    setAdding(true)
    setMsg(null)
    try {
      const data = await callHqPeople('', {
        method: 'POST',
        body: JSON.stringify({ email, name: addName.trim() || undefined }),
      })
      setMsg(
        data.message ||
          `Added ${email} as ${data.hqRole || 'member'}. Tell them to sign in with Microsoft and open Simple HQ.`,
      )
      setAddEmail('')
      setAddName('')
      await loadRoster()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to add person')
    } finally {
      setAdding(false)
    }
  }

  const removeFromHq = async (userId: string, email: string) => {
    if (
      !confirm(`Remove ${email} from Simple HQ? They keep Early Signals / company access.`)
    ) {
      return
    }
    setRemovingId(userId)
    setMsg(null)
    try {
      const data = await callHqPeople(`?user_id=${encodeURIComponent(userId)}`, {
        method: 'DELETE',
      })
      setMsg(data.message || `Removed ${email} from Simple HQ`)
      if (selected === userId) setSelected(null)
      await loadRoster()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to remove')
    } finally {
      setRemovingId(null)
    }
  }

  return (
    <div>
      <p className="hq-eyebrow">Admin</p>
      <h1 className="hq-title">People & leave</h1>
      <p className="hq-sub">
        Add staff to Simple HQ · entitlements · adjustments · bulk / CSV for ~300 scale.
      </p>

      <div className="hq-panel mb-4">
        <p className="hq-eyebrow">Add people</p>
        <p className="text-sm text-[var(--hq-muted)] mb-3">
          Joins them to The Simple Intel company and creates their HQ member role. They sign in with
          Microsoft, then open Simple HQ. Domains: @thesimpleintel.com or @thesimple.com.
        </p>
        <form className="hq-row items-end" onSubmit={(e) => void addPerson(e)}>
          <div className="hq-field flex-1 min-w-[12rem]">
            <label htmlFor="hq-add-email">Work email</label>
            <input
              id="hq-add-email"
              type="email"
              required
              value={addEmail}
              onChange={(e) => setAddEmail(e.target.value)}
              placeholder="name@thesimpleintel.com"
              autoComplete="off"
            />
          </div>
          <div className="hq-field flex-1 min-w-[10rem]">
            <label htmlFor="hq-add-name">Name (optional)</label>
            <input
              id="hq-add-name"
              value={addName}
              onChange={(e) => setAddName(e.target.value)}
              placeholder="Display name"
              autoComplete="off"
            />
          </div>
          <button type="submit" className="hq-btn hq-btn-primary" disabled={adding}>
            {adding ? 'Adding…' : 'Add to Simple HQ'}
          </button>
        </form>
      </div>

      <div className="flex flex-wrap gap-2 items-center mb-3">
        <input
          className="hq-search"
          placeholder="Search email or role…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label className="text-sm">
          Year{' '}
          <input
            type="number"
            className="border rounded px-2 py-1 w-24"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          />
        </label>
        <button type="button" className="hq-btn" onClick={exportReport}>
          Export roster CSV
        </button>
      </div>

      {msg && <div className="hq-ok-box">{msg}</div>}

      <div className="grid gap-4 md:grid-cols-2">
        <div className="hq-panel">
          <p className="hq-eyebrow">Roster ({filtered.length})</p>
          <div className="hq-table-wrap max-h-[420px] overflow-y-auto">
            <table className="hq-table">
              <thead>
                <tr>
                  <th>Bulk</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.user_id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={bulkIds.has(r.user_id)}
                        onChange={() => toggleBulk(r.user_id)}
                        aria-label={`Select ${r.email}`}
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="text-[var(--hq-accent)] font-semibold"
                        onClick={() => void loadPerson(r.user_id)}
                      >
                        {r.email}
                      </button>
                    </td>
                    <td>{r.role}</td>
                    <td>
                      {r.role !== 'admin' && (
                        <button
                          type="button"
                          className="hq-btn text-xs"
                          disabled={removingId === r.user_id}
                          onClick={() => void removeFromHq(r.user_id, r.email)}
                        >
                          {removingId === r.user_id ? '…' : 'Remove from HQ'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 border-t pt-3">
            <p className="hq-eyebrow">Bulk set entitlement</p>
            <div className="hq-row">
              <div className="hq-field">
                <label>Type</label>
                <select value={bulkType} onChange={(e) => setBulkType(e.target.value)}>
                  {types.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="hq-field">
                <label>Days</label>
                <input value={bulkDays} onChange={(e) => setBulkDays(e.target.value)} />
              </div>
            </div>
            <button type="button" className="hq-btn hq-btn-primary" onClick={() => void applyBulk()}>
              Apply to {bulkIds.size} selected
            </button>
          </div>

          <div className="mt-4 border-t pt-3">
            <p className="hq-eyebrow">CSV import</p>
            <p className="text-xs text-[var(--hq-muted)] mb-2">email,leave type name,days</p>
            <textarea
              className="w-full border rounded p-2 text-sm"
              rows={4}
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
            />
            <button type="button" className="hq-btn mt-2" onClick={() => void importCsv()}>
              Import entitlements
            </button>
          </div>
        </div>

        <div className="hq-panel">
          <p className="hq-eyebrow">Person detail</p>
          {!selected ? (
            <p className="hq-empty">Select someone from the roster.</p>
          ) : (
            <>
              <p className="font-semibold mb-3">
                {roster.find((r) => r.user_id === selected)?.email}
              </p>
              <div className="hq-table-wrap">
                <table className="hq-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Entitlement</th>
                      <th>Remaining</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {types.map((t) => (
                      <tr key={t.id}>
                        <td>{t.name}</td>
                        <td>
                          <input
                            className="w-20 border rounded px-1"
                            type="number"
                            step="0.5"
                            value={entitlements[t.id] ?? t.default_days}
                            onChange={(e) =>
                              setEntitlements((prev) => ({ ...prev, [t.id]: Number(e.target.value) }))
                            }
                          />
                        </td>
                        <td>{remaining[t.id] ?? '—'}</td>
                        <td>
                          <button
                            type="button"
                            className="hq-btn"
                            onClick={() => void saveEntitlement(t.id, entitlements[t.id] ?? t.default_days)}
                          >
                            Save
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 border-t pt-3">
                <p className="hq-eyebrow">Balance adjustment</p>
                <div className="hq-row">
                  <div className="hq-field">
                    <label>Type</label>
                    <select value={adjType} onChange={(e) => setAdjType(e.target.value)}>
                      {types.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="hq-field">
                    <label>Delta days</label>
                    <input value={adjDelta} onChange={(e) => setAdjDelta(e.target.value)} />
                  </div>
                </div>
                <div className="hq-field">
                  <label>Reason (required)</label>
                  <input value={adjReason} onChange={(e) => setAdjReason(e.target.value)} required />
                </div>
                <button type="button" className="hq-btn hq-btn-primary" onClick={() => void adjust()}>
                  Record adjustment
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
